// Long-tail SEO guides. Each drives <Guide /> via /guide/:slug.
// Real, genuinely useful advice — no keyword stuffing.

const STORE_LINE = "Visit us at 183 West Golf Rd, Schaumburg, IL — or shop online with shipping across the USA.";

export const GUIDES = {
  "what-to-wear-indian-wedding-winter-chicago": {
    seoTitle: "What to Wear to an Indian Wedding in a Chicago Winter",
    metaDescription:
      "A practical guide to Indian wedding guest outfits for a Chicago winter — warm fabrics, layering with shawls and jackets, and venue-proof styling for women and men.",
    kicker: "Style Guide · Winter Weddings",
    h1: "What to Wear to an Indian Wedding in a Chicago Winter",
    date: "2026-10-09",
    readTime: "4 min read",
    intro: "A Chicago winter wedding means sub-zero wind between the car and the venue — then three hours of dancing indoors. Here's how to dress for both without compromising the look.",
    sections: [
      {
        h: "Choose fabrics that hold warmth",
        body: [
          "Silk, velvet and heavier georgette are your friends from November through March. A silk or brocade saree, a velvet-trimmed lehenga, or an Anarkali in a thicker weave keeps you warm during the baraat photos outside and still breathes once the dance floor fills up.",
          "Skip tissue-thin chiffons as your main layer in January — save them for the dupatta instead.",
        ],
      },
      {
        h: "Layer like a local",
        body: [
          "A tailored shawl or stole in silk or pashmina-style weave is the classic winter move for women — it drapes over a saree or lehenga without hiding the outfit. For men, a kurta with a Nehru jacket is both the dress code and the insulation.",
          "Wear your real winter coat for the trip in — every venue has a coat check or a corner where coats pile up. The outfit underneath is what people see.",
        ],
      },
      {
        h: "Feet, cars and parking lots",
        body: [
          "Chicago parking lots in February are slush. If your outfit brushes the floor, arrive in boots and change into your heels or juttis inside — regulars do this all the time and nobody blinks.",
          "Keep a spare pair of flats in the car for the drive home; your feet will thank you at midnight.",
        ],
      },
      {
        h: "Colours for winter weddings",
        body: [
          "Deep jewel tones — maroon, emerald, navy, wine, royal purple — photograph beautifully against winter light and fit the season. Guests traditionally avoid red (reserved for the bride) and heavy white.",
        ],
      },
    ],
    cta: { label: "Shop Wedding Guest Outfits", to: "/indian-wedding-guest-clothing" },
    links: [
      { label: "Women's Wedding Guest Dresses", to: "/indian-wedding-guest-dresses" },
      { label: "Men's Wedding Guest Outfits", to: "/mens-wedding-guest-outfits" },
      { label: "Lehengas", to: "/shop?collection=lehenga-choli" },
    ],
    faqs: [
      { q: "Can I wear a saree to a winter wedding in Chicago?", a: "Absolutely — pick a silk or heavier-georgette saree, add a full-sleeve blouse, and bring a warm shawl for outdoors. The venue will be heated." },
      { q: "What should men wear to a winter Indian wedding?", a: "A kurta set with a Nehru jacket handles both the dress code and the temperature. Add a wool stole for outdoor photos." },
      { q: "Do you have winter-appropriate outfits in store?", a: "Yes — we stock silk sarees, velvet-detailed lehengas, full-sleeve blouses and kurta-jacket sets year-round. " + STORE_LINE },
    ],
  },

  "lehenga-vs-saree-vs-anarkali": {
    seoTitle: "Lehenga vs Saree vs Anarkali — A Wedding Guest's Guide",
    metaDescription:
      "Lehenga, saree or Anarkali? A simple guide to which Indian outfit suits which wedding event, body comfort level and budget — from a Chicago-area Indian boutique.",
    kicker: "Style Guide · Wedding Guests",
    h1: "Lehenga vs Saree vs Anarkali — Which One Is Yours?",
    date: "2026-10-09",
    readTime: "4 min read",
    intro: "Three classics, three very different wearing experiences. Here's an honest breakdown so you pick the outfit you'll actually enjoy for six hours — not just for the photos.",
    sections: [
      {
        h: "The lehenga — maximum impact, zero draping",
        body: [
          "A lehenga is a full skirt + blouse + dupatta set. There's nothing to drape or pin, so it's the most forgiving choice if you don't wear Indian clothing often. It twirls beautifully at the sangeet and reads 'celebration' instantly.",
          "Best for: sangeet, reception, and guests who want a statement look with zero learning curve. Watch for: full skirts take space on a crowded dance floor.",
        ],
      },
      {
        h: "The saree — timeless, with a learning curve",
        body: [
          "Six yards of elegance — and the most traditional choice for the ceremony itself. If you're new to draping, a readymade (pre-stitched) saree gives you the same look in under five minutes, no pins or pleating needed.",
          "Best for: the wedding ceremony, and guests who want the classic silhouette. Watch for: a long day in a saree is easier if you've practiced sitting and stairs in it once at home.",
        ],
      },
      {
        h: "The Anarkali — the comfortable middle",
        body: [
          "A long, flowing dress-style suit with churidar or palazzo bottoms. No draping, easy to sit, eat and dance in, and it suits every height. If you're attending multiple events across a wedding weekend, an Anarkali is the one you'll reach for twice.",
          "Best for: mehndi, haldi, daytime ceremonies, and anyone who wants comfort without looking underdressed.",
        ],
      },
      {
        h: "Still torn?",
        body: [
          "Match the outfit to the event card: ceremony → saree or Anarkali, sangeet → lehenga, reception → lehenga or a gown-style Anarkali. And if you're buying for a whole weekend, mix — one saree for the ceremony, one lehenga for the sangeet.",
        ],
      },
    ],
    cta: { label: "Shop Wedding Guest Outfits", to: "/indian-wedding-guest-clothing" },
    links: [
      { label: "Lehengas", to: "/shop?collection=lehenga-choli" },
      { label: "Sarees", to: "/shop?collection=sarees" },
      { label: "Readymade Sarees", to: "/shop?collection=readymade-saree" },
    ],
    faqs: [
      { q: "Is a lehenga or saree better for a wedding guest?", a: "For the ceremony, a saree is the most traditional. For sangeet and reception, a lehenga is easier to move and dance in. You can't go wrong with either — comfort decides." },
      { q: "What's easiest to wear if I've never worn Indian clothing?", a: "An Anarkali or a readymade pre-stitched saree. Both go on like a dress — no draping, no pins." },
      { q: "Can I try all three before deciding?", a: "Yes — that's what we're here for. " + STORE_LINE },
    ],
  },

  "saree-blouse-measurement-guide": {
    seoTitle: "How to Measure for a Readymade Saree Blouse (Guide)",
    metaDescription:
      "Measure yourself for a readymade saree blouse in 5 minutes — bust, underbust, shoulder, sleeve and length, plus when an alteration beats a size up. Chicago-area boutique.",
    kicker: "Fit Guide · Saree Blouses",
    h1: "How to Measure for a Readymade Saree Blouse",
    date: "2026-10-09",
    readTime: "3 min read",
    intro: "A saree only looks as good as its blouse fits. Five measurements, five minutes, one soft tape — and you'll order readymade blouses with confidence.",
    sections: [
      {
        h: "The five measurements that matter",
        body: [
          "1. Bust — around the fullest part, tape level and snug, not tight. 2. Underbust — directly under the bust; this decides how the blouse sits. 3. Shoulder — tip of one shoulder to the other, across the back. 4. Sleeve length — shoulder tip to where you want the sleeve to end. 5. Blouse length — shoulder to where you want the hem (usually just above the saree's waist tuck).",
          "Wear the bra you'll wear with the saree while measuring — it changes the bust number more than most people expect.",
        ],
      },
      {
        h: "Between sizes? Alter, don't size up",
        body: [
          "A blouse that's close-but-not-perfect at the bust or waist is a 10-minute alteration; a blouse that's a full size too big gaps at the neck and no pin fixes that. Order the closest size down the chart and have it taken in — we do this in-house every day.",
          "If you buy from us, ask about alterations at checkout or in-store and we'll finish it to your measurements.",
        ],
      },
      {
        h: "Necklines and sleeves — quick chooser",
        body: [
          "Boat necks flatter narrower shoulders; V and sweetheart necklines lengthen the neck. Elbow sleeves are the most forgiving for daytime events; sleeveless and cap sleeves suit receptions. When in doubt, elbow-length with a boat or round neck is the universal safe pick.",
        ],
      },
    ],
    cta: { label: "Shop Readymade Blouses", to: "/shop?collection=readymade-blouse" },
    links: [
      { label: "Saree Blouses Guide Page", to: "/saree-blouses" },
      { label: "Sarees", to: "/shop?collection=sarees" },
      { label: "Readymade Sarees", to: "/shop?collection=readymade-saree" },
    ],
    faqs: [
      { q: "What if my blouse doesn't fit when it arrives?", a: "If it's close, a small alteration fixes most fit issues — we alter blouses in-house. Unworn, unaltered items are covered by our 14-day return policy." },
      { q: "Can you measure me in-store?", a: "Yes — visit the boutique and we'll measure you in a few minutes and match you to the right blouse size. " + STORE_LINE },
    ],
  },
};

export const getGuide = (slug) => GUIDES[slug] || null;
